import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Calendar, User, Home } from 'lucide-react';

export default function BookingApprovals() {
  const [pendingBookings, setPendingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const loadPendingBookings = async () => {
    try {
      const { data } = await bookingAPI.getPending();
      setPendingBookings(data.bookings || []);
    } catch (error) {
      toast.error('Failed to load pending bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingBookings();
  }, []);

  const handleApprove = async (bookingId) => {
    try {
      await bookingAPI.approve(bookingId);
      toast.success('Booking approved! Email sent to resident.');
      loadPendingBookings();
    } catch (error) {
      toast.error('Failed to approve booking');
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }
    try {
      await bookingAPI.reject(rejectingId, rejectReason.trim());
      toast.success('Booking rejected');
      setRejectingId(null);
      setRejectReason('');
      loadPendingBookings();
    } catch (error) {
      toast.error('Failed to reject booking');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <Card className="shadow-lg border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Pending Booking Approvals
        </CardTitle>
      </CardHeader>
      <CardContent>
        {pendingBookings.length === 0 ? (
          <p className="text-center text-slate-500 py-8" data-testid="no-pending-bookings">No pending bookings</p>
        ) : (
          <div className="space-y-3">
            {pendingBookings.map((booking) => (
              <div
                key={booking.id}
                data-testid={`pending-booking-${booking.id}`}
                className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-bold text-lg">{booking.facility_name}</h4>
                    <div className="space-y-1 mt-2">
                      <p className="text-sm flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{booking.user_name}</span>
                      </p>
                      <p className="text-sm flex items-center gap-2">
                        <Home className="w-4 h-4" />
                        <span>Villa {booking.villa_number}</span>
                      </p>
                      <p className="text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>
                          Check-in: {new Date(booking.date).toLocaleDateString()} at {booking.checkin_time}
                        </span>
                      </p>
                      <p className="text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>
                          Check-out: {new Date(booking.checkout_date).toLocaleDateString()} at {booking.checkout_time}
                        </span>
                      </p>
                      {booking.comments && (
                        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                          <p className="text-xs font-medium text-blue-700">Resident Comments:</p>
                          <p className="text-sm text-blue-800">{booking.comments}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-amber-100">Pending</Badge>
                </div>

                {rejectingId === booking.id ? (
                  <div className="space-y-2 p-3 bg-red-50 rounded-lg border border-red-200" data-testid={`reject-reason-form-${booking.id}`}>
                    <label className="text-sm font-medium text-red-800">Rejection Reason:</label>
                    <textarea
                      data-testid={`reject-reason-input-${booking.id}`}
                      className="w-full p-2 border border-red-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                      rows={2}
                      placeholder="Enter reason for rejecting this booking..."
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button
                        data-testid={`confirm-reject-${booking.id}`}
                        onClick={handleRejectSubmit}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        size="sm"
                        disabled={!rejectReason.trim()}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Confirm Reject
                      </Button>
                      <Button
                        data-testid={`cancel-reject-${booking.id}`}
                        onClick={() => { setRejectingId(null); setRejectReason(''); }}
                        variant="outline"
                        className="flex-1"
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      data-testid={`approve-booking-${booking.id}`}
                      onClick={() => handleApprove(booking.id)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      size="sm"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      data-testid={`reject-booking-${booking.id}`}
                      onClick={() => { setRejectingId(booking.id); setRejectReason(''); }}
                      variant="outline"
                      className="flex-1 text-red-600 hover:bg-red-50"
                      size="sm"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
